import axios from "axios";

// Environment-based URL configuration
const getBaseURL = () => {
  // Check if we're in development or production
  let url;
  if (process.env.NODE_ENV === 'development') {
    // Local development environment
    url = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
  } else {
    // Production environment
    url = process.env.REACT_APP_BACKEND_URL || "https://lafunza.com";
  }
  
  // Ensure the URL ends with a slash
  return url.endsWith('/') ? url : url + '/';
};

// Function to get CSRF token from cookies (Firefox compatible)
const getCsrfToken = () => {
  let csrfToken = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Handle both 'csrftoken=' and 'csrftoken =' formats
      if (cookie.substring(0, 10) === 'csrftoken=' || cookie.indexOf('csrftoken=') !== -1) {
        const equalIndex = cookie.indexOf('=');
        if (equalIndex !== -1) {
          csrfToken = decodeURIComponent(cookie.substring(equalIndex + 1));
          break;
        }
      }
    }
  }
  return csrfToken;
};

const baseURL = getBaseURL();
console.log('Axios baseURL:', baseURL); // Debug log

const instance = axios.create({
  baseURL: baseURL,
  withCredentials: true, // Important for CSRF cookies
  // Firefox-specific timeout
  timeout: 30000,
});

// Add a request interceptor to include JWT token, CSRF token, and fix URL paths
instance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Set Content-Type for non-FormData requests
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // Add CSRF token for non-GET requests (Firefox compatible)
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
      // Also try alternative header name for Firefox
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    // Fix old account URLs to use new API structure
    if (config.url) {
      if (config.url.startsWith('/accounts/')) {
        config.url = '/api' + config.url;
      } else if (config.url.startsWith('accounts/')) {
        config.url = 'api/' + config.url;
      }
    }
    
    // Debug log the final URL being requested
    console.log('Axios request URL:', config.baseURL + config.url);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${getBaseURL()}api/token/refresh/`,
            { refresh: refreshToken }
          );
          
          const newAccessToken = response.data.access;
          localStorage.setItem('access_token', newAccessToken);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_type');
          localStorage.removeItem('user_email');
          window.location.href = '/Login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default instance;

/*
 * Unified Axios Configuration
 * 
 * This configuration automatically detects the environment:
 * - Development (NODE_ENV=development): Uses localhost:8000
 * - Production (NODE_ENV=production): Uses 185.97.146.165 (no port)
 * 
 * You can override these defaults by setting REACT_APP_BACKEND_URL environment variable
 * 
 * Features:
 * - Automatic JWT token inclusion in requests
 * - Automatic token refresh on 401 errors
 * - Automatic redirect to login on refresh failure
 */


